import { ChangeEvent } from 'react';
import { ButtonPrimary, ButtonSecondary } from '@components/button';
import { Input } from '@components/input';

import { DEFAULT_AVAILABLE_TYPES } from '../constants';
import { FormFieldRowInputType, FormFieldRowInputTypeLabels, FormFieldRowValue } from '../form-field-row.type';

export interface FormFieldRowEditorProps {
  value: FormFieldRowValue;
  onChange: (next: FormFieldRowValue) => void;
  onCancel: () => void;
  onSave: () => void;
  saveLabel: string;
  cancelLabel: string;
  availableTypes?: readonly FormFieldRowInputType[];
  typeLabels?: FormFieldRowInputTypeLabels;
  namePlaceholder?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  nameError?: boolean;
  minError?: boolean;
  maxError?: boolean;
}

const INPUT_CLASS = 'h-10 text-body-s! hover:bg-neutral-0! dark:hover:bg-neutral-70!';

const DEFAULT_TYPE_LABELS: Required<FormFieldRowInputTypeLabels> = {
  text: 'Text',
  number: 'Number',
};

export const FormFieldRowEditor = ({
  value,
  onChange,
  onCancel,
  onSave,
  saveLabel,
  cancelLabel,
  availableTypes = DEFAULT_AVAILABLE_TYPES,
  typeLabels,
  namePlaceholder = 'Custom field',
  minPlaceholder = 'Min',
  maxPlaceholder = 'Max',
  nameError = false,
  minError = false,
  maxError = false,
}: FormFieldRowEditorProps) => {
  const resolvedTypeLabels: Required<FormFieldRowInputTypeLabels> = {
    text: typeLabels?.text ?? DEFAULT_TYPE_LABELS.text,
    number: typeLabels?.number ?? DEFAULT_TYPE_LABELS.number,
  };
  const offeredTypes = availableTypes.includes(value.type) ? availableTypes : [...availableTypes, value.type];
  const hasTypeChoice = offeredTypes.length > 1;
  const renderedTypeOptions = offeredTypes.map((type) => ({ value: type, label: resolvedTypeLabels[type] }));

  const handleNameChange = (next: string) => onChange({ ...value, name: next });

  const handleTypeChange = (next: FormFieldRowInputType) => {
    if (next === value.type) return;
    onChange(
      next === 'text' ? { type: 'text', name: value.name } : { type: 'number', name: value.name, min: '', max: '' }
    );
  };

  return (
    <>
      <div className='flex w-full items-center'>
        <div className='flex min-w-0 flex-1 items-center gap-1 py-2 pl-3 pr-1'>
          <div className='min-w-0 flex-1'>
            <Input
              type='text'
              value={value.name}
              onChange={(event: ChangeEvent<HTMLInputElement>) => handleNameChange(event.target.value)}
              placeholder={namePlaceholder}
              className={INPUT_CLASS}
              hasError={nameError}
              data-testid='form-field-row-name-input'
            />
          </div>
        </div>

        {/* Content-sized cluster (not flex-1): the two fixed-width buttons already eat most of a 50/50
            split — so the name input flexes and the type select keeps its intrinsic (widest-option)
            width. `shrink-0` stops it collapsing into its own chevron at narrow drawer widths. */}
        <div className='flex shrink-0 items-center gap-2 py-2 pl-1 pr-3'>
          {/* pr-8 (not px-3) so the text clears the native dropdown arrow — a smaller right padding
              overrides the UA's arrow-clearance and the value runs under the chevron (DS Select uses pr-8). */}
          {hasTypeChoice && (
            <select
              value={value.type}
              onChange={(event) => handleTypeChange(event.target.value as FormFieldRowInputType)}
              className='h-10 shrink-0 rounded border border-soft bg-primary pl-3 pr-8 text-body-s text-secondary'
              aria-label='Field type'
              data-testid='form-field-row-type-select'>
              {renderedTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
          <ButtonSecondary variant='s' onClick={onCancel} className='w-20 shrink-0' data-testid='form-field-row-cancel'>
            {cancelLabel}
          </ButtonSecondary>
          <ButtonPrimary variant='s' onClick={onSave} className='w-20 shrink-0' data-testid='form-field-row-confirm'>
            {saveLabel}
          </ButtonPrimary>
        </div>
      </div>

      {value.type === 'number' && (
        <div className='flex w-1/2 items-center gap-1 py-2 pl-3 pr-1'>
          <div className='min-w-0 flex-1'>
            <Input
              type='number'
              value={value.min}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onChange({ ...value, min: event.target.value })}
              placeholder={minPlaceholder}
              className={INPUT_CLASS}
              hasError={minError}
              data-testid='form-field-row-min-input'
            />
          </div>
          <div className='min-w-0 flex-1'>
            <Input
              type='number'
              value={value.max}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onChange({ ...value, max: event.target.value })}
              placeholder={maxPlaceholder}
              className={INPUT_CLASS}
              hasError={maxError}
              data-testid='form-field-row-max-input'
            />
          </div>
        </div>
      )}
    </>
  );
};
